/**
 * Hook pour l'historique de trésorerie
 * Charge les mouvements financiers réalisés d'un mois passé
 * Migré vers React Query (useQuery)
 * @module hooks/useTresorerieHistorique
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/queryKeys';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { FluxTresorerie, CategorieDecaissement } from '@/types/tresorerie';
import type { ChargeFix } from '@/types/charges-fixes.types';

// =============================================================================
// Types
// =============================================================================

export interface MoisHistorique {
  mois: string; // 'yyyy-MM'
  label: string; // 'Janvier 2026'
  encaissements: FluxTresorerie[];
  decaissements: FluxTresorerie[];
  total_encaissements: number;
  total_decaissements: number;
  variation: number; // encaissements - décaissements
  // Versions HT pour le switch HT/TTC
  total_encaissements_ht: number;
  total_decaissements_ht: number;
  variation_ht: number;
}

export interface UseTresorerieHistoriqueReturn {
  // Données du mois sélectionné
  moisData: MoisHistorique | null;
  isLoading: boolean;
  error: string | null;

  // Navigation
  anneesDisponibles: number[];
  moisDisponibles: { value: string; label: string }[];
  anneeSelectionnee: number;
  moisSelectionne: string; // format 'yyyy-MM'

  // Actions
  setAnnee: (annee: number) => void;
  setMois: (mois: string) => void;
}

// =============================================================================
// Constantes
// =============================================================================

const MOIS_LABELS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

// =============================================================================
// Utilitaire charges fixes (réutilisé de useTresorerie)
// =============================================================================

function chargeApplicableCeMois(charge: ChargeFix, moisCible: string): boolean {
  if (charge.frequence === 'mensuel') return true;

  const createdAt = new Date(charge.created_at);
  const moisCreation = createdAt.getMonth();
  const [, moisStr] = moisCible.split('-');
  const moisCibleIndex = parseInt(moisStr, 10) - 1;

  if (charge.frequence === 'trimestriel') {
    return ((moisCibleIndex - moisCreation + 12) % 12) % 3 === 0;
  }

  if (charge.frequence === 'annuel') {
    return moisCibleIndex === moisCreation;
  }

  return false;
}

// =============================================================================
// Fonction de chargement des données d'un mois (extraite pour la queryFn)
// =============================================================================

async function loadMoisHistorique(
  entrepriseId: string,
  moisSelectionne: string
): Promise<MoisHistorique> {
  const [annee, moisNum] = moisSelectionne.split('-').map(Number);
  const dateDebut = startOfMonth(new Date(annee, moisNum - 1, 1));
  const dateFin = endOfMonth(dateDebut);
  const dateDebutStr = format(dateDebut, 'yyyy-MM-dd');
  const dateFinStr = format(dateFin, 'yyyy-MM-dd');

  // 1. Charger les encaissements réalisés (paiements clients encaissés)
  const { data: paiementsData, error: paiementsError } = await supabase
    .from('paiements_clients')
    .select(`
      id,
      montant,
      date_paiement,
      type,
      chantier_id,
      chantiers!inner(nom_chantier, entreprise_id)
    `)
    .eq('chantiers.entreprise_id', entrepriseId)
    .eq('statut', 'encaisse')
    .gte('date_paiement', dateDebutStr)
    .lte('date_paiement', dateFinStr);

  if (paiementsError) throw paiementsError;

  // 2. Charger les frais chantier
  const { data: fraisData, error: fraisError } = await supabase
    .from('frais_chantier')
    .select(`
      id,
      montant_ht,
      montant_ttc,
      date_frais,
      type_frais,
      description,
      chantier_id,
      chantiers!inner(nom_chantier, entreprise_id)
    `)
    .eq('chantiers.entreprise_id', entrepriseId)
    .gte('date_frais', dateDebutStr)
    .lte('date_frais', dateFinStr);

  if (fraisError) throw fraisError;

  // 3. Charger les factures fournisseurs
  const { data: facturesData, error: facturesError } = await supabase
    .from('factures_fournisseurs')
    .select(`
      id,
      montant_ht,
      montant_ttc,
      date_facture,
      fournisseur,
      categorie,
      chantier_id,
      chantiers!inner(nom_chantier, entreprise_id)
    `)
    .eq('chantiers.entreprise_id', entrepriseId)
    .gte('date_facture', dateDebutStr)
    .lte('date_facture', dateFinStr);

  if (facturesError) throw facturesError;

  // 4. Charger les heures supplémentaires
  const { data: heuresSupData, error: heuresSupError } = await supabase
    .from('heures_supplementaires')
    .select(`
      id,
      cout_total,
      date_travail,
      chantier_id,
      chantiers!inner(nom_chantier, entreprise_id),
      membres_equipe(prenom, nom)
    `)
    .eq('chantiers.entreprise_id', entrepriseId)
    .gte('date_travail', dateDebutStr)
    .lte('date_travail', dateFinStr);

  if (heuresSupError) throw heuresSupError;

  // 5. Charger les ajustements (avenants/remises)
  const { data: ajustementsData, error: ajustementsError } = await supabase
    .from('ajustements_paiement')
    .select(`
      id,
      type,
      montant,
      date_ajustement,
      description,
      chantier_id,
      chantiers!inner(nom_chantier, entreprise_id)
    `)
    .eq('chantiers.entreprise_id', entrepriseId)
    .gte('date_ajustement', dateDebutStr)
    .lte('date_ajustement', dateFinStr);

  if (ajustementsError) throw ajustementsError;

  // 6. Charger les charges fixes actives créées avant la fin du mois
  const { data: chargesFixesData, error: chargesFixesError } = await supabase
    .from('charges_fixes')
    .select('*')
    .eq('entreprise_id', entrepriseId)
    .eq('actif', true)
    .lte('created_at', dateFinStr);

  if (chargesFixesError) throw chargesFixesError;

  // Construire les encaissements
  const encaissements: FluxTresorerie[] = [];

  // Paiements clients (montant = TTC, pas de colonne HT en DB)
  (paiementsData || []).forEach((p: any) => {
    const montantTtc = Math.abs(Number(p.montant));
    encaissements.push({
      id: p.id,
      type: 'encaissement',
      categorie: 'facture_client',
      libelle: `Paiement ${p.type || 'client'}`,
      montant: montantTtc,
      montant_ht: Math.round(montantTtc / 1.2 * 100) / 100,
      date_prevue: p.date_paiement,
      statut: 'realise',
      reference_id: p.id,
      reference_table: 'paiements_clients',
      chantier_id: p.chantier_id,
      chantier_nom: p.chantiers?.nom_chantier,
    });
  });

  // Avenants (ajustements positifs)
  (ajustementsData || []).filter((a: any) => a.type === 'avenant').forEach((a: any) => {
    const montantTtc = Math.abs(Number(a.montant));
    encaissements.push({
      id: a.id,
      type: 'encaissement',
      categorie: 'avenant',
      libelle: a.description || 'Avenant',
      montant: montantTtc,
      montant_ht: Math.round(montantTtc / 1.2 * 100) / 100,
      date_prevue: a.date_ajustement,
      statut: 'realise',
      reference_id: a.id,
      reference_table: 'ajustements_paiement',
      chantier_id: a.chantier_id,
      chantier_nom: a.chantiers?.nom_chantier,
    });
  });

  // Construire les décaissements
  const decaissements: FluxTresorerie[] = [];

  // Frais chantier
  (fraisData || []).forEach((f: any) => {
    let categorie: CategorieDecaissement = 'frais_annexes';
    if (f.type_frais === 'Matériaux') categorie = 'frais_materiaux';
    else if (f.type_frais === 'Sous-traitant') categorie = 'frais_sous_traitant';

    const montantHt = Math.abs(Number(f.montant_ht));
    const montantTtc = f.montant_ttc ? Math.abs(Number(f.montant_ttc)) : Math.round(montantHt * 1.2 * 100) / 100;

    decaissements.push({
      id: f.id,
      type: 'decaissement',
      categorie,
      libelle: f.description || f.type_frais,
      montant: montantTtc,
      montant_ht: montantHt,
      date_prevue: f.date_frais,
      statut: 'realise',
      reference_id: f.id,
      reference_table: 'frais_chantier',
      chantier_id: f.chantier_id,
      chantier_nom: f.chantiers?.nom_chantier,
    });
  });

  // Factures fournisseurs
  (facturesData || []).forEach((f: any) => {
    const montantHt = Math.abs(Number(f.montant_ht));
    const montantTtc = f.montant_ttc ? Math.abs(Number(f.montant_ttc)) : Math.round(montantHt * 1.2 * 100) / 100;

    decaissements.push({
      id: f.id,
      type: 'decaissement',
      categorie: 'facture_fournisseur',
      libelle: f.fournisseur || f.categorie || 'Facture fournisseur',
      montant: montantTtc,
      montant_ht: montantHt,
      date_prevue: f.date_facture,
      statut: 'realise',
      reference_id: f.id,
      reference_table: 'factures_fournisseurs',
      chantier_id: f.chantier_id,
      chantier_nom: f.chantiers?.nom_chantier,
    });
  });

  // Heures supplémentaires (pas de TVA : HT = TTC)
  (heuresSupData || []).forEach((h: any) => {
    const montant = Math.abs(Number(h.cout_total));
    const membreNom = h.membres_equipe ? `${h.membres_equipe.prenom} ${h.membres_equipe.nom}` : 'Membre';
    decaissements.push({
      id: h.id,
      type: 'decaissement',
      categorie: 'heures_sup',
      libelle: `Heures sup - ${membreNom}`,
      montant,
      montant_ht: montant,
      date_prevue: h.date_travail,
      statut: 'realise',
      reference_id: h.id,
      reference_table: 'heures_supplementaires',
      chantier_id: h.chantier_id,
      chantier_nom: h.chantiers?.nom_chantier,
    });
  });

  // Remises (ajustements négatifs)
  (ajustementsData || []).filter((a: any) => a.type === 'remise').forEach((a: any) => {
    const montantTtc = Math.abs(Number(a.montant));
    decaissements.push({
      id: a.id,
      type: 'decaissement',
      categorie: 'remise',
      libelle: a.description || 'Remise',
      montant: montantTtc,
      montant_ht: Math.round(montantTtc / 1.2 * 100) / 100,
      date_prevue: a.date_ajustement,
      statut: 'realise',
      reference_id: a.id,
      reference_table: 'ajustements_paiement',
      chantier_id: a.chantier_id,
      chantier_nom: a.chantiers?.nom_chantier,
    });
  });

  // Charges fixes applicables au mois historique
  const chargesFixes = (chargesFixesData as ChargeFix[]) || [];
  const chargesFixesMois = chargesFixes.filter(cf => {
    const chargeCreatedAt = new Date(cf.created_at);
    if (chargeCreatedAt > dateFin) return false;
    return chargeApplicableCeMois(cf, moisSelectionne);
  });

  chargesFixesMois.forEach(cf => {
    decaissements.push({
      id: `charge-fixe-${cf.id}-${moisSelectionne}`,
      type: 'decaissement',
      categorie: 'charges_fixes',
      libelle: cf.nom,
      montant: cf.montant_ttc,
      montant_ht: cf.montant_ht,
      date_prevue: `${moisSelectionne}-${String(cf.jour_prelevement).padStart(2, '0')}`,
      statut: 'realise',
      reference_id: cf.id,
      reference_table: 'charges_fixes',
      chantier_id: undefined,
      chantier_nom: undefined,
    });
  });

  // Calculer les totaux TTC
  const totalEncaissements = encaissements.reduce((sum, e) => sum + e.montant, 0);
  const totalDecaissements = decaissements.reduce((sum, d) => sum + d.montant, 0);
  const variation = totalEncaissements - totalDecaissements;

  // Calculer les totaux HT
  const totalEncaissementsHt = encaissements.reduce((sum, e) => sum + e.montant_ht, 0);
  const totalDecaissementsHt = decaissements.reduce((sum, d) => sum + d.montant_ht, 0);
  const variationHt = totalEncaissementsHt - totalDecaissementsHt;

  // Générer le label du mois
  const moisLabel = format(dateDebut, 'MMMM yyyy', { locale: fr });
  const moisLabelCapitalized = moisLabel.charAt(0).toUpperCase() + moisLabel.slice(1);

  return {
    mois: moisSelectionne,
    label: moisLabelCapitalized,
    encaissements,
    decaissements,
    total_encaissements: totalEncaissements,
    total_decaissements: totalDecaissements,
    variation,
    total_encaissements_ht: totalEncaissementsHt,
    total_decaissements_ht: totalDecaissementsHt,
    variation_ht: variationHt,
  };
}

// =============================================================================
// Hook
// =============================================================================

export function useTresorerieHistorique(entrepriseId: string | null): UseTresorerieHistoriqueReturn {
  const { toast } = useToast();

  // État de navigation (UI locale)
  const [dateCreationEntreprise, setDateCreationEntreprise] = useState<Date | null>(null);
  const [anneeSelectionnee, setAnneeSelectionnee] = useState<number>(new Date().getFullYear());
  const [moisSelectionne, setMoisSelectionne] = useState<string>('');

  // Charger la date de création de l'entreprise (initialise la navigation)
  useEffect(() => {
    if (!entrepriseId) return;

    const loadDateCreation = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('entreprises')
          .select('created_at')
          .eq('id', entrepriseId)
          .single();

        if (fetchError) throw fetchError;

        if (data?.created_at) {
          const dateCreation = new Date(data.created_at);
          setDateCreationEntreprise(dateCreation);

          // Initialiser sur le mois précédent par défaut
          const moisPrecedent = subMonths(new Date(), 1);
          const anneeMoisPrec = moisPrecedent.getFullYear();
          const moisKeyPrec = format(moisPrecedent, 'yyyy-MM');

          if (moisPrecedent < dateCreation) {
            setAnneeSelectionnee(dateCreation.getFullYear());
            setMoisSelectionne(format(dateCreation, 'yyyy-MM'));
          } else {
            setAnneeSelectionnee(anneeMoisPrec);
            setMoisSelectionne(moisKeyPrec);
          }
        }
      } catch (err) {
        console.error('Erreur chargement date création entreprise:', err);
      }
    };

    loadDateCreation();
  }, [entrepriseId]);

  // Générer la liste des années disponibles
  const anneesDisponibles = useMemo(() => {
    if (!dateCreationEntreprise) return [new Date().getFullYear()];

    const anneeDebut = dateCreationEntreprise.getFullYear();
    const anneeFin = new Date().getFullYear();
    const annees: number[] = [];

    for (let a = anneeFin; a >= anneeDebut; a--) {
      annees.push(a);
    }

    return annees;
  }, [dateCreationEntreprise]);

  // Générer la liste des mois disponibles pour l'année sélectionnée
  const moisDisponibles = useMemo(() => {
    const maintenant = new Date();
    const anneeActuelle = maintenant.getFullYear();
    const moisActuel = maintenant.getMonth();

    const mois: { value: string; label: string }[] = [];

    let premierMois = 0;
    if (dateCreationEntreprise && anneeSelectionnee === dateCreationEntreprise.getFullYear()) {
      premierMois = dateCreationEntreprise.getMonth();
    }

    let dernierMois = 11;
    if (anneeSelectionnee === anneeActuelle) {
      dernierMois = moisActuel - 1;
      if (dernierMois < 0) {
        return [];
      }
    }

    for (let m = dernierMois; m >= premierMois; m--) {
      const moisKey = `${anneeSelectionnee}-${String(m + 1).padStart(2, '0')}`;
      mois.push({
        value: moisKey,
        label: MOIS_LABELS[m],
      });
    }

    return mois;
  }, [anneeSelectionnee, dateCreationEntreprise]);

  // ============================================================================
  // QUERY : Données du mois sélectionné
  // ============================================================================

  const { data: moisData = null, isLoading, error } = useQuery({
    queryKey: [...queryKeys.tresorerie.historique(entrepriseId!), moisSelectionne],
    queryFn: () => loadMoisHistorique(entrepriseId!, moisSelectionne),
    enabled: !!entrepriseId && !!moisSelectionne,
    staleTime: 10 * 60 * 1000, // 10 min (données historiques changent rarement)
    meta: {
      onError: () => {
        toast({
          title: 'Erreur',
          description: "Impossible de charger l'historique de trésorerie.",
          variant: 'destructive',
        });
      },
    },
  });

  // Actions de navigation
  const setAnnee = useCallback((annee: number) => {
    setAnneeSelectionnee(annee);

    const maintenant = new Date();
    const anneeActuelle = maintenant.getFullYear();
    const moisActuel = maintenant.getMonth();

    let nouveauMois: number;
    if (annee === anneeActuelle) {
      nouveauMois = moisActuel - 1;
      if (nouveauMois < 0) nouveauMois = 0;
    } else if (annee < anneeActuelle) {
      nouveauMois = 11;
    } else {
      nouveauMois = 0;
    }

    if (dateCreationEntreprise && annee === dateCreationEntreprise.getFullYear()) {
      const moisCreation = dateCreationEntreprise.getMonth();
      if (nouveauMois < moisCreation) {
        nouveauMois = moisCreation;
      }
    }

    const nouveauMoisKey = `${annee}-${String(nouveauMois + 1).padStart(2, '0')}`;
    setMoisSelectionne(nouveauMoisKey);
  }, [dateCreationEntreprise]);

  const setMois = useCallback((mois: string) => {
    setMoisSelectionne(mois);
  }, []);

  // Convertir l'erreur React Query en string
  const errorMsg = error
    ? ((error as any)?.message || 'Erreur lors du chargement')
    : null;

  return {
    moisData,
    isLoading,
    error: errorMsg,
    anneesDisponibles,
    moisDisponibles,
    anneeSelectionnee,
    moisSelectionne,
    setAnnee,
    setMois,
  };
}
